import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../components/ui/dialog";
import { ui } from "../../components/ui";
import { cn } from "../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  detailSections,
  fieldLabel,
  fieldValue,
  statusLabel,
  statusOptions,
  tenantRequestId,
  tenantRequestStatusOf,
} from "./tenantRequestFields";
import { TenantRequestStatusBadge } from "./TenantRequestStatusBadge";

const ONBOARDING_FIELDS = [
  ["adminUsername", "Admin username", "text"],
  ["adminPassword", "Admin password", "password"],
  ["karyaPocName", "Karya POC name", "text"],
  ["karyaPocEmail", "Karya POC email", "email"],
  ["karyaPocPhone", "Karya POC phone", "tel"],
];
const emptyDetails = () => Object.fromEntries(ONBOARDING_FIELDS.map(([key]) => [key, ""]));
export function TenantRequestDetailsDialog({
  request,
  busy = false,
  error = "",
  onChangeStatus,
  onOpenChange,
}) {
  const { t } = useTranslation();
  const status = tenantRequestStatusOf(request);

  /** What has been picked, until it is saved or the card moves on. */
  const [chosen, setChosen] = useState(status);
  const [details, setDetails] = useState(emptyDetails);
  useEffect(() => {
    setChosen(status);
    setDetails(emptyDetails());
  }, [request, status]);

  const submit = (event) => {
    event.preventDefault();
    if (busy || chosen === status) return;
    const payload = chosen === "Onboarded"
      ? Object.fromEntries(ONBOARDING_FIELDS.map(([key]) => [
          key, key === "adminPassword" ? details[key] : details[key].trim(),
        ]))
      : undefined;
    if (payload && Object.values(payload).some((value) => !value.trim())) return;
    onChangeStatus(chosen, payload);
  };

  if (!request) return null;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        {/* A profile of fifty answers, several of them pasted-in lists: the
            card takes as much of the window as it can so they are read rather
            than scrolled past. */}
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex h-[92vh] w-[calc(100vw-1.5rem)] max-w-[1180px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-20px_rgba(10,18,48,0.45)] focus:outline-hidden">
          <div className="flex items-start justify-between gap-3 border-b border-(--color-text-border)/70 px-4 py-4 sm:px-6">
            <div className="min-w-0">
              {/* The status reads as part of the name: what this is, and where
                  it has got to. It wraps below on a narrow screen. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <DialogTitle className="min-w-0 truncate text-xl font-bold text-foreground sm:text-2xl">
                  {fieldValue(request, "organizationName") || "—"}
                </DialogTitle>
                <TenantRequestStatusBadge status={status} />
              </div>
              <p className="mt-1 text-[12px] per-file-hint">
                {[
                  `${t("common.id")} ${tenantRequestId(request)}`,
                  fieldValue(request, "createdAt"),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <DialogPrimitive.Close className="grid h-9 w-9 shrink-0 place-items-center rounded-xl per-file-hint transition hover:bg-workspace/60 hover:text-primary">
              <X className="h-5 w-5" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            {/* A rule between the groups: fifty answers read as six parts. */}
            <div className="divide-y divide-(--color-text-border)/70">
              {detailSections(request).map((section) => (
                <section key={section.title} className="py-6 first:pt-0 last:pb-0">
                  <h3 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-primary">
                    {section.title}
                  </h3>

                  <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                    {section.fields.map((key) => (
                      <div key={key} className="min-w-0">
                        <dt className="text-[11px] font-bold uppercase tracking-wider per-file-hint">
                          {fieldLabel(key)}
                        </dt>
                        {/* Several of these are pasted in as lists — the
                            holidays, the departments — and their line breaks
                            are the answer. */}
                        <dd className="mt-1 whitespace-pre-line wrap-break-word text-sm text-foreground">
                          {fieldValue(request, key)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
            <form id="tenant-request-status-form" onSubmit={submit}>
              {chosen === "Onboarded" && (
                <section className="mt-6 border-t border-(--color-text-border)/70 pt-6">
                  <h3 className="mb-3 text-sm font-bold text-primary">Onboarding details</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {ONBOARDING_FIELDS.map(([key, label, type]) => (
                      <div key={key} className="min-w-0">
                        <label htmlFor={key} className="mb-1 block text-xs font-semibold text-foreground">
                          {label} <span aria-hidden="true">*</span>
                        </label>
                        <input
                          id={key}
                          name={key}
                          type={type}
                          required
                          pattern={type === "email" ? undefined : ".*\\S.*"}
                          autoComplete={type === "password" ? "new-password" : "off"}
                          disabled={busy}
                          value={details[key]}
                          onChange={(event) => setDetails((current) => ({ ...current, [key]: event.target.value }))}
                          className={cn(ui.input, "h-10 w-full")}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </form>
          </div>

          {/* Save the status and required onboarding details. */}
          <div className="border-t border-(--color-text-border)/70 px-4 py-3 sm:px-6">
            {error ? (
              <p className="mb-2 text-[12px] font-semibold text-rose-600">{error}</p>
            ) : null}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
              <span className="text-xs font-semibold text-foreground">
                {t("status.status")}
              </span>
              <Select value={chosen || undefined} disabled={busy} onValueChange={setChosen}>
                <SelectTrigger className="h-10 w-full rounded-lg border border-(--color-text-border) bg-white text-sm sm:w-[220px]">
                  <SelectValue placeholder={t("status.status")} />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {statusOptions(status).map((value) => (
                    <SelectItem key={value} value={value}>
                      {statusLabel(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Nothing to save until the pick differs from what is stored. */}
              <button
                type="submit"
                form="tenant-request-status-form"
                disabled={busy || chosen === status}
                className={cn(ui.primaryBtn, "h-10 w-full px-6 sm:w-auto")}
              >
                {busy ? t("tenantRequests.updating") : t("formActions.save")}
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export default TenantRequestDetailsDialog;
